/**
 * @fileoverview Match Entity page for the matching step of the Listings Launch flow.
 *
 * @author Stephanie Yoon (steyoon@yext.com)
 */


import {debounce} from 'goog:goog.functions';

import {ExternalAccountsContext}
  from '/js/yext/externalaccounts/shared/listingslaunch/externalaccountscontext';
import {Divider} from '/src/com/yext/listingslaunchstorm/js/components/common/divider';
import {EntityDetails} from '/src/com/yext/listingslaunchstorm/js/components/common/entitydetails';
import {ErrorCallout} from '/src/com/yext/listingslaunchstorm/js/components/common/errorcallout';
import {SubHeader} from '/src/com/yext/listingslaunchstorm/js/components/common/subheader';
import {GroupMenuController} from '/src/com/yext/listingslaunchstorm/js/components/groupmenucontroller';
import {GroupTextInput} from '/src/com/yext/listingslaunchstorm/js/components/grouptextinput';
import {MatchResults} from '/src/com/yext/listingslaunchstorm/js/components/matchresults';
import {ListingsLaunchContext} from '/src/com/yext/listingslaunchstorm/js/contexts/listingslaunchcontextprovider';
import {EntityInfo} from '/src/com/yext/listingslaunchstorm/js/data/entityinfo';
import {GetGoogleGroupsRequest} from '/src/com/yext/listingslaunchstorm/js/data/getgooglegroupsrequest';
import {GetGoogleMatchesRequest} from '/src/com/yext/listingslaunchstorm/js/data/getgooglematchesrequest';
import {GoogleGroup} from '/src/com/yext/listingslaunchstorm/js/data/googlegroup';
import {MatchedListing} from '/src/com/yext/listingslaunchstorm/js/data/matchedlisting';
import {ValidateStoreCodeRequest} from '/src/com/yext/listingslaunchstorm/js/data/validatestorecoderequest';
import {ValidateStoreCodeResponse} from '/src/com/yext/listingslaunchstorm/js/data/validatestorecoderesponse';
import {VerificationMethod} from '/src/com/yext/listingslaunchstorm/js/data/verificationmethod';
import {VerifiedListing} from '/src/com/yext/listingslaunchstorm/js/data/verifiedlisting';
import {getGoogleGroups} from '/src/com/yext/listingslaunchstorm/js/datasource/getgooglegroups';
import {getGoogleMatches} from '/src/com/yext/listingslaunchstorm/js/datasource/getgooglematches';
import {getValidateStoreCodeMatch} from '/src/com/yext/listingslaunchstorm/js/datasource/getvalidatestorecodematch';
import {CalloutBanner, CalloutBannerType} from '/ui/components/calloutbanner/calloutbanner';

import * as styles from '/src/com/yext/listingslaunchstorm/js/components/styles/matchentity.module.scss';

/**
 * Match entity step instructions, entity details, and entity selection
 * @param {{
 *  entity: EntityInfo,
 * }} props
 * @return {React.ReactNode}
*/
export const MatchEntity = ({entity}) => {
  const {
    googleGroups,
    setGoogleGroups,
    matchedListings,
    setMatchedListings,
    setVerifiedListings,
    currentMatches,
  } = React.useContext(ListingsLaunchContext);
  const {
    publisherIdToExternalAccount,
  } = React.useContext(ExternalAccountsContext);
  const linkedAccount = publisherIdToExternalAccount.get(715);
  const businessId = window['businessId'];
  const [loadingGroups, setLoadingGroups] = React.useState(false);
  const [useTextInput, setUseTextInput] = React.useState(false);
  const [groupId, setGroupId] = React.useState(null);
  const [groupDetails, setGroupDetails] = React.useState(null);
  const [possibleMatches, setPossibleMatches] = React.useState([]);
  const [loadingPossibleMatches, setLoadingPossibleMatches] = React.useState(false);
  const [matchedListing, setMatchedListing] = React.useState(null);

  const [storeCodeMatchedListing, setStoreCodeMatchedListing] = React.useState(new MatchedListing({
    entityId: null,
    externalId: null,
    groupId: null,
    displayAddress: '',
    isVerified: false,
  }));
  const [createdNewListing, setCreatedNewListing] = React.useState(new MatchedListing({
    entityId: entity.id,
    externalId: '',
    groupId: null,
    displayAddress: '',
    isVerified: false,
  }));
  const [storeCodeInvalidated, setStoreCodeInvalidated] = React.useState(false);
  const [showStoreCodeInput, setShowStoreCodeInput] = React.useState(false);
  const [storeCodeInputValue, setStoreCodeInputValue] = React.useState('');
  const [storeCodeMatchedListingChanged, setStoreCodeMatchedListingChanged] = React.useState(false);

  const [errorMessage, setErrorMessage] = React.useState('');
  const [showError, setShowError] = React.useState(false);

  const onSelectMatch = match => {
    if (match.entityId !== null) {
      if (match.externalId !== storeCodeMatchedListing.externalId) {
        setShowStoreCodeInput(false);
      } else {
        setShowStoreCodeInput(true);
      }
      updateMatchedListing(match);
    } else {
      setShowStoreCodeInput(true);
      setMatchedListing(null);
      setMatchedListings(getOtherMatchedListings);
    }
  };

  const getOtherMatchedListings = React.useCallback(currentMatchedListings => {
    if (currentMatchedListings.length > 0) {
      const newMatchedListings = currentMatchedListings.filter(match => match.entityId !== entity.id);
      // If the lengths do not match, this means a match for this entity has already been selected
      // and we are changing the selection to a different match.
      if (newMatchedListings.length !== currentMatchedListings.length) {
        // Reset verification now that we have chosen a new match.
        setVerifiedListings(
          verifiedListings => verifiedListings.filter(v => v.entityId !== entity.id));
      }

      return newMatchedListings;
    }

    return matchedListings;
  }, [matchedListings, entity.id, setVerifiedListings]);

  const getOtherVerifiedListings = React.useCallback(currentVerifiedListings => {
    const newVerifiedListings = currentVerifiedListings.filter(listing => listing.entityId !== entity.id);
    return newVerifiedListings;
  }, [entity.id]);

  const updateMatchedListing = React.useCallback(match => {
    setMatchedListing(match);
    setMatchedListings(currentMatchedListings => [...getOtherMatchedListings(currentMatchedListings), match]);
    if (match.externalId === '') {
      setVerifiedListings(currentVerifiedListings => [...getOtherVerifiedListings(currentVerifiedListings), new VerifiedListing({
        entityId: match.entityId,
        method: VerificationMethod.DISABLED,
      })]);
    } else if (match.isVerified) {
      setVerifiedListings(currentVerifiedListings => [...getOtherVerifiedListings(currentVerifiedListings), new VerifiedListing({
        entityId: match.entityId,
        method: VerificationMethod.PREVIOUSLY_VERIFIED,
      })]);
    }
  }, [getOtherMatchedListings, getOtherVerifiedListings, setMatchedListings, setVerifiedListings]);

  // if the card's listing info is equal to the matchedListing's info, then the card is selected
  // or if matchedListing hasn't been set yet and the card is the store code card and
  // the store code card has been clicked, then the card is selected
  const isSelectedMatch = match => {
    return (matchedListing !== null && match.entityId === matchedListing.entityId
      && match.externalId === matchedListing.externalId)
      || (matchedListing === null && match.entityId === null && showStoreCodeInput);
  };

  const onRetry = () => {
    setShowError(false);
    if (googleGroups.length === 0) {
      setLoadingGroups(true);
      setGroupId(null);
    } else {
      setLoadingPossibleMatches(true);
      setPossibleMatches([]);
      setMatchedListing(null);
    }
  };

  React.useEffect(() => {
    if (googleGroups.length === 0) {
      setLoadingGroups(true);
      setGroupId(null);
    }
  }, [googleGroups, setLoadingGroups, setGroupId]);

  React.useEffect(() => {
    const abortController = new AbortController();
    if (loadingGroups) {
      const request = new GetGoogleGroupsRequest({
        businessId: businessId,
        externalAccountId: linkedAccount.id,
      });
      getGoogleGroups(request, abortController.signal).then(response => {
        if (response.error != '') {
          setLoadingGroups(false);
          setErrorMessage(response.error);
          setShowError(true);
        } else {
          setUseTextInput(response.useTextInput);
          setLoadingGroups(false);
          if (response.useTextInput) {
            const currentMatch = matchedListings.find(match => match.entityId === entity.id);
            if (currentMatch) {
              setGroupId(currentMatch.groupId);
              setGroupDetails(currentMatch.groupId.toString());
            }
          } else {
            setGoogleGroups(response.groups);

            const groupIds = response.groups.map(group => group.id);
            setMatchedListings(matchedListings.filter(match => groupIds.includes(match.groupId)));
          }
        }
      }).catch(aborted => {
        if (!aborted) {
          setLoadingGroups(false);
          setShowError(true);
        }
      });
    }
    return () => abortController.abort();
  }, [businessId, entity.id, loadingGroups, linkedAccount, matchedListings, setGoogleGroups, setMatchedListings]);

  React.useEffect(() => {
    if (groupId !== null) {
      setLoadingPossibleMatches(true);
      setStoreCodeInvalidated(false);
      setStoreCodeMatchedListing(new MatchedListing({
        entityId: null,
        externalId: null,
        groupId: null,
        displayAddress: '',
        isVerified: false,
      }));
      setCreatedNewListing(new MatchedListing({
        entityId: entity.id,
        externalId: '',
        groupId: groupId,
        displayAddress: yext.msg('New Listing'),
        isVerified: false,
      }));
    }
  }, [groupId, entity]);

  React.useEffect(() => {
    if (googleGroups.length > 0) {
      const initialMatch = matchedListings.find(match => match.entityId === entity.id);
      if (initialMatch !== undefined) {
        const initialGroup = googleGroups.find(googleGroup => googleGroup.id === initialMatch.groupId);
        if (initialGroup !== undefined) {
          setGroupId(initialGroup.id);
          const initialGroupDetails = initialGroup.name + ' (' + initialGroup.id + ')';
          setGroupDetails(initialGroupDetails);
        }
      }
    }
  }, [googleGroups, entity] /* eslint-disable-line react-hooks/exhaustive-deps */);

  React.useEffect(() => {
    const abortController = new AbortController();
    if (loadingPossibleMatches) {
      const request = new GetGoogleMatchesRequest({
        businessId: businessId,
        entityId: entity.id,
        externalAccountId: linkedAccount.id,
        groupId: groupId,
      });
      getGoogleMatches(request, abortController.signal).then(response => {
        if (response.hasError) {
          setErrorMessage(response.customErrorMessage ?? '');
          setLoadingPossibleMatches(false);
          setShowError(true);
        } else {
          const getGoogleMatchesResponse = response.value;
          setPossibleMatches(getGoogleMatchesResponse.matches);
          setLoadingPossibleMatches(false);
          const initialMatch = matchedListings.find(match => match.entityId === entity.id && groupId === match.groupId);
          if (initialMatch !== undefined) {
            updateMatchedListing(initialMatch);
          } else {
            setMatchedListing(null);
            setMatchedListings(getOtherMatchedListings);
          }
        }
      }).catch(aborted => {
        if (!aborted) {
          setLoadingPossibleMatches(false);
          setShowError(true);
        }
      });
    }
    return () => abortController.abort();
  }, [businessId, entity, groupId, loadingPossibleMatches, linkedAccount, getOtherMatchedListings, setMatchedListings, matchedListings, updateMatchedListing]);

  React.useEffect(() => {
    if (showStoreCodeInput && storeCodeMatchedListingChanged) {
      setMatchedListing(storeCodeMatchedListing);
      setMatchedListings(currentMatchedListings => [...getOtherMatchedListings(currentMatchedListings), storeCodeMatchedListing]);
      setStoreCodeMatchedListingChanged(false);
    }
  }, [showStoreCodeInput, storeCodeMatchedListingChanged, storeCodeMatchedListing, setMatchedListings, getOtherMatchedListings]);

  const onStoreCodeChange = storeCode => {
    setStoreCodeInputValue(storeCode);
    debouncedValidateStoreCode(storeCode);
  };

  const debouncedValidateStoreCode = React.useMemo(
    () => {
      const validateStoreCode = storeCode => {
        setStoreCodeInvalidated(false);
        setStoreCodeMatchedListing(new MatchedListing({
          entityId: null,
          externalId: null,
          groupId: null,
          displayAddress: '',
          isVerified: false,
        }));

        if (storeCode === '') {
          return;
        }
        const abortController = new AbortController();
        const request = new ValidateStoreCodeRequest({
          businessId: businessId,
          externalAccountId: linkedAccount.id,
          groupId: groupId,
          storeCode: storeCode,
        });
        getValidateStoreCodeMatch(request, abortController.signal).then(response => {
          if (response.hasError) {
            setErrorMessage(response.customErrorMessage ?? '');
            setShowError(true);
            setStoreCodeInputValue('');
          } else {
            const validateStoreCodeMatchResponse = response.value;
            if (validateStoreCodeMatchResponse?.match) {
              const responseMatch = new MatchedListing({
                entityId: entity.id,
                externalId: validateStoreCodeMatchResponse.match.externalId,
                groupId: groupId,
                displayAddress: validateStoreCodeMatchResponse.match.addressTokens[0],
                isVerified: validateStoreCodeMatchResponse.match.isVerified,
              });

              setStoreCodeMatchedListing(responseMatch);
              setStoreCodeMatchedListingChanged(true);
              updateMatchedListing(responseMatch);
            } else {
              setStoreCodeInvalidated(true);
            }
          }
        }).catch(aborted => {
          if (!aborted) {
            setShowError(true);
          }
        });
        return () => abortController.abort();
      };
      return debounce(validateStoreCode, 1000);
    }, [businessId, groupId, entity.id, linkedAccount.id, updateMatchedListing]);

  return (
    <div key={entity.id}>
      <Divider />
      {showError
        ? <><ErrorCallout onRetry={onRetry} message={errorMessage}/></>
        : <>
          <div className={styles.calloutWarning}>
            <CalloutBanner type={CalloutBannerType.NEUTRAL}>
              {yext.msg('If you do not complete this step, you will not be able to manage your Google Business Profile listing through us.')}
            </CalloutBanner>
          </div>
          <SubHeader mainText={yext.msg('Your Entity')} />
          <EntityDetails entity={entity}/>
          {(useTextInput && !loadingGroups)
            ? (
              <GroupTextInput
                linkedAccountId={linkedAccount.id}
                initialGroupId={groupId}
                onValidInput={id => {
                  const group = new GoogleGroup({
                    id,
                    name: id.toString(),
                  });

                  setGroupDetails(group.name);
                  setGroupId(group.id);
                }}
              />
            ) : (
              <GroupMenuController
                selected={groupDetails}
                onSelect={(group, groupDetails) => {
                  setGroupDetails(groupDetails);
                  setGroupId(group.id);
                }}
                loadingGroups={loadingGroups}
                googleGroups={googleGroups}
              />
            )
          }
          <MatchResults
            groupId={groupId}
            entityId={entity.id}
            loadingPossibleMatches={loadingPossibleMatches}
            possibleMatches={possibleMatches}
            storeCodeMatchedListing={storeCodeMatchedListing}
            createdNewListing={createdNewListing}
            showStoreCodeInput={showStoreCodeInput}
            storeCodeInvalidated={storeCodeInvalidated}
            onStoreCodeChange={onStoreCodeChange}
            storeCodeInputValue={storeCodeInputValue}
            onSelected={onSelectMatch}
            isSelected={isSelectedMatch}
            currentMatches={currentMatches}
          />
        </>}
    </div>
  );
};
